window.debounce = function (fn, wait) {
  let t;
  return function (...a) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, a), wait);
  };
};

function withAlpine(cb) {
  if (window.Alpine) cb(window.Alpine);
  else document.addEventListener('alpine:init', () => cb(window.Alpine));
}

withAlpine((Alpine) => {
Alpine.data('gaCartDrawer', (threshold) => ({
  threshold,
  open: false,
  updating: false,
  updatingKey: null,

  cart: { items: [], total_price: 0, item_count: 0 },

    thresholdCents: 0,
    giftThresholdCents: 0,
    nextDayThresholdCents: 0,
    giftAnimProgress: 0,

    milestoneEnabled: false,
    allowMultiTierGifts: false,
    m1ThresholdCents: 0,
    m2ThresholdCents: 0,
    giftWorthCents: 0,
    m1WorthCents: 0,
    m2WorthCents: 0,
    giftAutoAdd: false,
    tagCacheVersion: 0,

    close() {
      this.open = false;
    },

    isFreeGift(item) {
      if (item?.properties?._free_gift === 'true') return true;
      if (item?.properties?._free_gift_m1 === 'true') return true;
      if (item?.properties?._free_gift_m2 === 'true') return true;
      return false;
    },

    isMilestoneGift(item, mi) {
      const attr = mi === 0 ? '_free_gift_m1' : '_free_gift_m2';
      return item?.properties?.[attr] === 'true';
    },

    get qualifyingSubtotalCents() {
      return (this.cart.items || []).reduce((sum, item) => {
        if (this.isFreeGift(item)) return sum;
        return sum + (item.final_line_price || 0);
      }, 0);
    },

    // Bir kademenin kendi tabanı. Tag'li kademede yalnızca o tag'i taşıyan
    // satırlar sayılır, tag'siz kademede tüm sepet.
    tierSubtotalCents(milestoneIndex) {
      this.tagCacheVersion;

      if (!window.GiftEngine) return this.qualifyingSubtotalCents;
      return window.GiftEngine.tierSubtotalCents(this.cart, milestoneIndex);
    },

    // The tag condition lives in GiftEngine so the cart UI, auto-add and the
    // cart transform all answer "is this tier unlocked?" the same way.
    // tagCacheVersion is referenced only to recompute once tags have loaded.
    tierQualified(milestoneIndex) {
      this.tagCacheVersion;

      if (!window.GiftEngine) {
        const cents = milestoneIndex === null
          ? this.giftThresholdCents
          : (milestoneIndex === 0 ? this.m1ThresholdCents : this.m2ThresholdCents);
        return !!cents && this.qualifyingSubtotalCents >= cents;
      }

      return milestoneIndex === null
        ? window.GiftEngine.isQualified(this.cart)
        : window.GiftEngine.isMilestoneQualified(this.cart, milestoneIndex);
    },

    get giftQualified() {
      if (this.milestoneEnabled) return false;
      return this.tierQualified(null);
    },

    get hasFreeGift() {
      if (this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isFreeGift(item));
    },

    get m1Qualified() {
      return this.milestoneEnabled && this.tierQualified(0);
    },

    get m2Qualified() {
      return this.milestoneEnabled && this.tierQualified(1);
    },

    get hasAnyMilestoneGift() {
      if (!this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isMilestoneGift(item, 0) || this.isMilestoneGift(item, 1));
    },

    get hasM1Gift() {
      if (!this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isMilestoneGift(item, 0));
    },

    get hasM2Gift() {
      if (!this.milestoneEnabled) return false;
      return (this.cart.items || []).some((item) => this.isMilestoneGift(item, 1));
    },

    get remainingToM1Cents() {
      return Math.max(this.m1ThresholdCents - this.tierSubtotalCents(0), 0);
    },

    get remainingToM2Cents() {
      return Math.max(this.m2ThresholdCents - this.tierSubtotalCents(1), 0);
    },

    // --- Tag conditions ---------------------------------------------------
    // A tier can be restricted to tagged products ("£45 of Makeup"), in which
    // case only those lines count towards it. The bar has to say so, otherwise
    // the customer reads "£14.61 to go" and adds anything.
    tierTags(milestoneIndex) {
      this.tagCacheVersion;

      const spec = window.GiftEngine?.eligibleTags;
      if (!spec) return [];

      const entry =
        milestoneIndex === null || milestoneIndex === undefined
          ? spec.legacy
          : spec.milestones?.[milestoneIndex];

      return entry?.configured || [];
    },

    // "makeup" -> "Makeup", ["makeup","skincare"] -> "Makeup or Skincare"
    tierTagLabel(milestoneIndex) {
      const tags = this.tierTags(milestoneIndex).map((tag) =>
        tag.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      );

      if (tags.length === 0) return '';
      if (tags.length === 1) return tags[0];
      return tags.slice(0, -1).join(', ') + ' or ' + tags[tags.length - 1];
    },

    stopHint(stop) {
      if (stop.qualified) {
        if (stop.cents <= 0) return `${stop.longLabel} applies to every order.`;
        return stop.tagLabel
          ? `Unlocked — your bag has ${this.formatMoney(stop.cents)}+ of ${stop.tagLabel}.`
          : `Unlocked at ${this.formatMoney(stop.cents)}.`;
      }

      if (stop.tagLabel) {
        return `Only ${stop.tagLabel} products count towards this one. Add ${this.formatMoney(stop.remaining)} more of ${stop.tagLabel} to unlock it.`;
      }

      return `Add ${this.formatMoney(stop.remaining)} more to unlock ${stop.longLabel}.`;
    },

    // --- Progress bar geometry -------------------------------------------
    // Milestones are spaced evenly along the track instead of proportionally
    // to their value, so close thresholds (e.g. £45 gift vs £50 next day
    // against a £250 max) never stack on top of each other.
    get progressStops() {
      const cartSubtotal = this.qualifyingSubtotalCents;
      const raw = [];

      raw.push({
        key: 'shipping',
        cents: this.thresholdCents || 0,
        subtotal: cartSubtotal,
        qualified: cartSubtotal >= (this.thresholdCents || 0),
        label: 'Free Shipping',
        longLabel: 'Free Shipping',
        icon: '🚚',
        color: '#837ae8',
        above: false,
        worthCents: 0,
        tagLabel: ''
      });

      if (this.milestoneEnabled) {
        if (this.m1ThresholdCents > 0) {
          raw.push({
            key: 'm1',
            cents: this.m1ThresholdCents,
            subtotal: this.tierSubtotalCents(0),
            qualified: this.m1Qualified,
            label: 'Gift',
            longLabel: 'Gift',
            icon: '🎁',
            color: '#f96979',
            above: false,
            worthCents: this.m1WorthCents,
            tagLabel: this.tierTagLabel(0)
          });
        }
        if (this.m2ThresholdCents > 0) {
          raw.push({
            key: 'm2',
            cents: this.m2ThresholdCents,
            subtotal: this.tierSubtotalCents(1),
            qualified: this.m2Qualified,
            label: 'Gift',
            longLabel: 'Gift',
            icon: '🎁',
            color: '#e855a0',
            above: false,
            worthCents: this.m2WorthCents,
            tagLabel: this.tierTagLabel(1)
          });
        }
      } else if (this.giftThresholdCents > 0) {
        raw.push({
          key: 'gift',
          cents: this.giftThresholdCents,
          subtotal: this.tierSubtotalCents(null),
          qualified: this.giftQualified,
          label: 'Gift',
          longLabel: 'Gift',
          icon: '🎁',
          color: '#f96979',
          above: false,
          worthCents: this.giftWorthCents,
          tagLabel: this.tierTagLabel(null)
        });
      }

      raw.push({
        key: 'nextDay',
        cents: this.nextDayThresholdCents || 0,
        subtotal: cartSubtotal,
        qualified: cartSubtotal >= (this.nextDayThresholdCents || 0),
        label: 'Free Next Day',
        longLabel: 'Free Next Day Delivery',
        icon: '⚡',
        color: '#22c55e',
        above: true,
        worthCents: 0,
        tagLabel: ''
      });

      raw.sort((a, b) => a.cents - b.cents);

      // A zero threshold is already met before the first penny is spent, so it
      // is pinned to the start of the track; the real thresholds share the rest.
      const zero = raw.filter((s) => s.cents <= 0).map((s) => ({ ...s, position: 0 }));
      const positive = raw.filter((s) => s.cents > 0);
      const n = positive.length;

      return zero
        .concat(positive.map((stop, i) => ({ ...stop, position: ((i + 1) / n) * 100 })))
        .map((stop) => {
          const withRemaining = { ...stop, remaining: Math.max(stop.cents - stop.subtotal, 0) };
          return { ...withRemaining, hint: this.stopHint(withRemaining) };
        });
    },

    // The first stop the cart hasn't earned yet — what every message is about.
    get nextGoal() {
      return this.progressStops.find((stop) => !stop.qualified) || null;
    },

    // The stop right before it, so the message can lead with "X unlocked!".
    get lastUnlocked() {
      const stops = this.progressStops;
      const index = stops.findIndex((stop) => !stop.qualified);
      if (index === -1) return stops.length ? stops[stops.length - 1] : null;
      return index > 0 ? stops[index - 1] : null;
    },

    // Tooltips sit above the dot. Alpine merges object styles with whatever is
    // already inline, so x-show keeps control of display.
    tipStyle(stop) {
      const style = {
        position: 'absolute',
        bottom: 'calc(100% + 12px)',
        width: '190px',
        maxWidth: '70vw',
        background: '#fff',
        color: '#4b5563',
        border: '1px solid #f3f4f6',
        borderRadius: '10px',
        padding: '8px 10px',
        fontSize: '10px',
        lineHeight: '1.4',
        fontWeight: '500',
        letterSpacing: '0',
        textTransform: 'none',
        textAlign: 'left',
        boxShadow: '0 8px 24px rgba(17,24,39,.14)',
        pointerEvents: 'none'
      };

      // The ones near the ends are edge aligned so they can't spill out of the
      // drawer.
      if (stop.position <= 20) return { ...style, left: '-10px' };
      if (stop.position >= 80) return { ...style, right: '-10px' };
      return { ...style, left: '50%', transform: 'translateX(-50%)' };
    },

    get hasTagRule() {
      return this.progressStops.some((stop) => stop.tagLabel);
    },

    get remainingToFreeShipCents() {
      return Math.max(this.thresholdCents - this.qualifyingSubtotalCents, 0);
    },

    get remainingToGiftCents() {
      return Math.max(this.giftThresholdCents - this.tierSubtotalCents(null), 0);
    },
    get remainingToNextDayCents() {
      return Math.max(this.nextDayThresholdCents - this.qualifyingSubtotalCents, 0);
    },

    get nextDayQualified() {
      return !!this.nextDayThresholdCents && this.qualifyingSubtotalCents >= this.nextDayThresholdCents;
    },

    // Only cart-wide stops can be measured against the cart subtotal, so they
    // alone drive the fill; a tag restricted tier counts a different pot of money
    // and is a marker on the track, nothing more. Letting one take part in the
    // walk froze the bar at the stop before it however large the cart grew,
    // because that tier's own pot stayed at zero.
    get progressPercent() {
      const stops = this.progressStops;
      if (!stops.length) return 0;
      if (stops.every((stop) => stop.qualified)) return 100;

      // Fallback for a setup where every tier is tag restricted: with no cart-wide
      // threshold to scale against, the nearest tier drives the fill from its pot.
      const cartWide = stops.filter((stop) => !stop.tagLabel && stop.cents > 0);
      const scale = cartWide.length ? cartWide : stops.filter((stop) => stop.cents > 0);

      const goal = scale.find((stop) => !stop.qualified);
      if (!goal) return 100;

      const passed = scale.filter((stop) => stop.qualified && stop.cents < goal.cents);
      const anchor = passed.length ? passed[passed.length - 1] : null;
      const base = anchor && !anchor.tagLabel ? anchor.cents : 0;
      const basePos = anchor ? anchor.position : 0;
      const span = goal.cents - base;
      const ratio = span > 0 ? Math.min(Math.max((goal.subtotal - base) / span, 0), 1) : 0;

      return basePos + ratio * (goal.position - basePos);
    },

    get subtotal() {
      return this.cart.total_price / 100;
    },

    formatMoney(cents) {
      return '£' + (cents / 100).toFixed(2);
    },

    init() {
        this.thresholdCents = Math.round((Number(this.threshold) || 0) * 100);
        this.giftThresholdCents = Math.round(
          (Number(window.GIFT_ENGINE_CONFIG?.threshold) || 0) * 100
        );
        this.nextDayThresholdCents = Math.round(
          (Number(window.GA_NEXT_DAY_THRESHOLD) || 50) * 100
        );

        this.milestoneEnabled = !!window.GIFT_ENGINE_CONFIG?.milestoneEnabled;
        this.allowMultiTierGifts = !!window.GIFT_ENGINE_CONFIG?.allowMultiTierGifts;
        this.giftAutoAdd = !!window.GIFT_ENGINE_CONFIG?.giftAutoAdd;
        const milestones = window.GIFT_ENGINE_CONFIG?.milestones || [];
        this.m1ThresholdCents = Math.round((Number(milestones[0]?.threshold) || 0) * 100);
        this.m2ThresholdCents = Math.round((Number(milestones[1]?.threshold) || 0) * 100);
        this.giftWorthCents = Number(window.GIFT_ENGINE_CONFIG?.giftWorthCents) || 0;
        this.m1WorthCents = Number(milestones[0]?.worthCents) || 0;
        this.m2WorthCents = Number(milestones[1]?.worthCents) || 0;

      this.fetchCart();

      window.addEventListener('toggle-cart', () => {
        this.fetchCart();
        this.open = true;
      });

      window.addEventListener('cart:refresh', () => {
        this.fetchCart();
      });

      window.addEventListener('gift:tags-loaded', () => {
        this.tagCacheVersion++;
      });
    },

    async fetchCart() {
      try {
        const data = await (await fetch('/cart.js')).json();

        this.cart.items = (data.items || []).map((item) => ({
          product_id: item.product_id,
          key: item.key,
          id: item.variant_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          variant_title: item.variant_title,
          product_title: item.product_title,
          final_line_price: item.final_line_price,
          original_line_price: item.original_line_price,
          final_unit_price: item.final_price ?? item.price,
          original_unit_price: item.original_price,
          image: item.image,
          url: item.url,
          handle: item.handle,
          price: item.price,
          original_price: item.original_price,
          properties: item.properties || {}
        }));

        this.cart.total_price = data.total_price || 0;
        this.cart.item_count = data.item_count || 0;

        window.GiftEngine?.ensureTagsLoaded(data).then(() => this.tagCacheVersion++);

        window.GA_CART = data;

        window.dispatchEvent(
          new CustomEvent('cart:updated', { detail: data })
        );

        this.$nextTick(() => {
          const target = this.progressPercent;
          let start = this.giftAnimProgress;

          const step = () => {
            start += (target - start) * 0.15;
            this.giftAnimProgress = start;

            if (Math.abs(target - start) > 0.3) {
              requestAnimationFrame(step);
            } else {
              this.giftAnimProgress = target;
            }
          };

          step();
        });
      } catch (e) {
        console.error('[GA] fetchCart error', e);
      }
    },

    async _changeCart(key, qty) {
      this.updating = true;
      this.updatingKey = key;

      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty }),
        });

        await this.fetchCart();
      } catch (e) {
        console.error('[GA] cart change error', e);
      } finally {
        this.updating = false;
        this.updatingKey = null;
      }
    },

    async incrementItem(item) {
      if (this.updating) return;

      if (this.isFreeGift(item)) {
        this.updating = true;
        this.updatingKey = item.key;

        try {
          if (window.GiftEngine?.addPaidCopyOfGiftVariant) {
            await window.GiftEngine.addPaidCopyOfGiftVariant(item.variant_id);
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.variant_id,
                quantity: 1
              }),
            });
          }

          await this.fetchCart();
        } catch (e) {
          console.error('[GA] increment free gift error', e);
        } finally {
          this.updating = false;
          this.updatingKey = null;
        }

        return;
      }

      this._changeCart(item.key, item.quantity + 1);
    },

    decrementItem(item) {
      if (item.quantity <= 1) {
        this.removeItem(item.key);
        return;
      }

      this._changeCart(item.key, item.quantity - 1);
    },

    updateQuantity(key, q) {
      q < 1 ? this.removeItem(key) : this._changeCart(key, q);
    },

    removeItem(key) {
      this._changeCart(key, 0);
    },

    giftLabel(item) {
      if (item?.properties?._free_gift_m1 === 'true') return 'Free gift (Tier 1)';
      if (item?.properties?._free_gift_m2 === 'true') return 'Free gift (Tier 2)';
      return 'Free gift included';
    },
  }));
});

withAlpine((Alpine) => {
  Alpine.data('gaAddToCart', (cfg = {}) => ({
    selectedVariant: cfg.initialVariantId || null,
    quantity: 1,
    adding: false,
    showDynamicCheckout: cfg.showDynamicCheckout ?? true,
    showGiftCardRecipient: cfg.showGiftCardRecipient ?? false,

    decrement() {
      if (this.quantity > 1) this.quantity--;
    },

    increment() {
      this.quantity++;
    },

    buildPayload(fd) {
      const p = {};
      fd.forEach((v, k) => {
        if (v === '' || v === null) return;
        if (p[k] === undefined) p[k] = v;
        else {
          if (!Array.isArray(p[k])) p[k] = [p[k]];
          p[k].push(v);
        }
      });
      return p;
    },

    async submitForm(evt) {
      if (this.adding) return;
      this.adding = true;

      try {
        const form = evt.target.closest('form');

        if (form && this.selectedVariant) {
          const idInput = form.querySelector('[name="id"]');
          if (idInput) idInput.value = this.selectedVariant;
        }

        const payload = this.buildPayload(new FormData(form));

        await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });

        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              attributes: {
                platform: 'web',
              },
            }),
          });
        } catch (err) {
          console.error('[GA] cart attribute update error', err);
        }

        window.dispatchEvent(new CustomEvent('toggle-cart'));
      } catch (e) {
        console.error('[GA] add-to-cart error', e);
      } finally {
        this.adding = false;
      }
    },
  }));
});